import { useMemo, RefObject } from 'react';
import { mixpanel, bigQuery } from '@fiverr-private/obs';
import { useEnteredView } from '@fiverr-private/hooks';
import { getContext } from '@fiverr-private/fiverr_context';
import {
    createMixpanelBannerEvent,
    createBigQueryBannerClickEvent,
    createBigQueryBannerObserveEvent,
} from './useEvents.utils';
import { MixpanelBannerEventType } from './useEvents.constants';

/**
 * Custom React hook `useEvents` options interface.
 */
export interface UseEventsOptions {
    /**
     * React ref object with observable element.
     */
    ref: RefObject<HTMLDivElement>;
    /**
     * Fiverr Sub Category slug.
     */
    partnerSlug: string;
    /**
     * Fiverr Sub Category id.
     */
    partnerId: number;
    /**
     * Search query or tag name which was used to query banner data.
     */
    searchTerm: string;
    /**
     * Host application name.
     */
    pageName: string;
}

/**
 * Custom React hook to use Banner's BI events.
 */
export const useEvents = ({
    ref: innerRef,
    partnerSlug,
    partnerId,
    searchTerm,
    pageName,
}: UseEventsOptions) => {
    const { onObserve, onClick } = useMemo(() => {
        const {
            userId,
            organizationId,
            queryParameters: { ref_ctx_id: refCtxId },
        } = getContext();

        const onObserve = () => {
            mixpanel.track(
                MixpanelBannerEventType.Show,
                createMixpanelBannerEvent({
                    userId,
                    searchTerm,
                    partnerSlug,
                })
            );
            bigQuery.send(
                createBigQueryBannerObserveEvent({
                    partnerSlug,
                    partnerId,
                    pageName,
                    organizationId,
                })
            );
        };

        const onClick = () => {
            mixpanel.track(
                MixpanelBannerEventType.Click,
                createMixpanelBannerEvent({
                    userId,
                    searchTerm,
                    partnerSlug,
                })
            );
            bigQuery.send(
                createBigQueryBannerClickEvent({
                    refCtxId,
                    partnerSlug,
                    partnerId,
                    pageName,
                    organizationId,
                })
            );
        };

        return {
            onObserve,
            onClick,
        };
    }, [partnerSlug, partnerId, searchTerm, pageName]);

    useEnteredView(innerRef, {
        threshold: 1,
        onEntered: onObserve,
    });

    return onClick;
};
